import { RefreshCw } from 'lucide-react';
import { useData } from '../context/DataContext';
import { useEffect, useState } from 'react';

interface CacheStatusProps {
  className?: string;
  refreshData?: () => Promise<void>;
}

export function CacheStatus({ className = '', refreshData }: CacheStatusProps) {
  // Poll lastRefreshed from localStorage every 30 seconds
  const [lastRefreshed, setLastRefreshed] = useState<number>(() => {
    const stored = localStorage.getItem('lastFetchTime');
    return stored ? Number(stored) : 0;
  });

  useEffect(() => {
    const interval = setInterval(() => {
      const stored = localStorage.getItem('lastFetchTime');
      setLastRefreshed(stored ? Number(stored) : 0);
    }, 30000); // 30 seconds
    // Listen for storage events (cross-tab and immediate update)
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'lastFetchTime') {
        setLastRefreshed(e.newValue ? Number(e.newValue) : 0);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  // Also update immediately on mount
  useEffect(() => {
    const stored = localStorage.getItem('lastFetchTime');
    setLastRefreshed(stored ? Number(stored) : 0);
  }, []);

  const now = Date.now();
  const minutesSinceLastFetch = lastRefreshed > 0 ? Math.round((now - lastRefreshed) / (1000 * 60)) : 0;
  const isExpired = lastRefreshed > 0 && (now - lastRefreshed) >= 30 * 60 * 1000;
  
  // Create tooltip text
  const getTooltipText = () => {
    if (lastRefreshed === 0) {
      return "No data loaded yet";
    }
    if (minutesSinceLastFetch >= 30) {
      return `Cache is ${minutesSinceLastFetch} minutes old - refresh your cache`;
    }
    return `Cache refreshed ${minutesSinceLastFetch} minutes ago`;
  };

  // Use refreshData from prop if provided, otherwise from context
  const context = useData();
  const handleRefresh = refreshData || context.refreshData;

  return (
    <button
      onClick={handleRefresh}
      className={`flex items-center space-x-2 px-3 py-2 rounded-md transition-colors border ${
        isExpired
          ? 'bg-red-100 text-red-800 hover:bg-red-200 border-red-800'
          : 'bg-action-surface text-[#148D82] border-[#148D82] hover:bg-[#dae9e7]'
      } ${className}`}
      title={getTooltipText()}
    >
      <span className="text-xs font-medium">Data Cache</span>
      <RefreshCw className="h-3 w-3" />
    </button>
  );
} 